<template>
  <section class="admin-content" id="single-appt-page">
    <Navbar />
    <main class="admin-main">

      <!-- Hero Header -->
      <div class="appt-hero">
        <div class="container">
          <div class="row align-items-center p-t-40 p-b-40">
            <div class="col-md-8">
              <div class="d-flex align-items-center mb-2">
                <a href="#" class="back-link mr-3" aria-label="Back" @click.prevent="$router.back()">
                  <i class="fe fe-arrow-left"></i>
                </a>
                <h2 class="hero-title mb-0">{{ isTele ? 'Telemedicine Consultation' : 'Appointment Details' }}</h2>
              </div>
              <div class="d-flex align-items-center" v-if="appoint.data">
                <span :class="'hero-status-badge ' + (appoint.data.status || 'pending')">
                  {{ appoint.data.status || 'pending' }}
                </span>
                <span class="hero-type ml-3" v-if="appoint.data.type">
                  <i :class="appoint.data.type === 'virtual' ? 'fe fe-video' : 'fe fe-map-pin'"></i>
                  {{ appoint.data.type }}
                </span>
              </div>
            </div>

            <!-- Quick Actions in Header -->
            <div class="col-md-4 text-right" v-if="appoint.data">
              <button
                v-if="appoint.data.type === 'virtual' && appoint.data.meeting_code"
                class="btn btn-join m-2"
                @click="joinMeeting"
              >
                <i class="fe fe-video mr-1"></i> Join Call
              </button>
              <button
                v-if="appoint.data.status !== 'completed' && appoint.data.status !== 'declined'"
                class="btn btn-notes-header"
                data-toggle="modal"
                data-target="#notesModal"
              >
                <i class="fe fe-edit-2 mr-1"></i> Add Notes
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading skeleton -->
      <div v-if="!appoint.data" class="container p-t-40">
        <div class="skeleton-card"></div>
      </div>

      <div class="container appt-body" v-if="appoint.data">

        <!-- Status action bar (telemedicine: only the consulting doctor) -->
        <div class="action-bar" v-if="hasActions && (!isTele || (tele && tele.is_consulting_doctor))">
          <div class="action-bar-inner">
            <p class="action-hint">
              <i class="mdi mdi-information-outline mr-1"></i>
              Update this appointment's status:
            </p>
            <div class="action-btns">
              <button
                v-if="canTransition('accepted')"
                class="action-btn accept"
                @click="updateStatus('accepted')"
                :disabled="isLoading"
              >
                <i class="fe fe-check mr-1"></i> Accept
              </button>
              <button
                v-if="canTransition('completed')"
                class="action-btn complete"
                @click="updateStatus('completed')"
                :disabled="isLoading"
              >
                <i class="fe fe-check-circle mr-1"></i> Mark Completed
              </button>
              <button
                v-if="canTransition('declined')"
                class="action-btn decline"
                @click="updateStatus('declined')"
                :disabled="isLoading"
              >
                <i class="fe fe-x mr-1"></i> Decline
              </button>
            </div>
          </div>
        </div>

        <div class="row">

          <!-- Left column -->
          <div class="col-lg-7 col-md-12">

            <!-- Appointment Info Card -->
            <div class="detail-card mb-4">
              <div class="detail-card-header">
                <i class="mdi mdi-calendar-check mr-2"></i> Appointment Info
              </div>
              <div class="detail-card-body">

                <div class="info-row">
                  <div class="info-icon-wrap blue">
                    <i class="fe fe-calendar"></i>
                  </div>
                  <div>
                    <p class="info-label">Date</p>
                    <p class="info-value">{{ appoint.data.appointDate | moment("dddd, MMMM Do YYYY") }}</p>
                  </div>
                </div>

                <div class="info-row">
                  <div class="info-icon-wrap purple">
                    <i class="fe fe-clock"></i>
                  </div>
                  <div>
                    <p class="info-label">Time</p>
                    <p class="info-value">{{ appoint.data.appoinTime }}</p>
                  </div>
                </div>

                <div class="info-row" v-if="appoint.data.professional">
                  <div class="info-icon-wrap teal">
                    <i class="fe fe-user"></i>
                  </div>
                  <div>
                    <p class="info-label">Attending Professional</p>
                    <p class="info-value">
                      {{ appoint.data.professional.firstname }} {{ appoint.data.professional.lastname }}
                    </p>
                  </div>
                </div>

                <div class="info-row">
                  <div class="info-icon-wrap orange">
                    <i :class="appoint.data.type === 'virtual' ? 'fe fe-video' : 'fe fe-map-pin'"></i>
                  </div>
                  <div>
                    <p class="info-label">Appointment Type</p>
                    <p class="info-value text-capitalize">{{ appoint.data.type || 'physical' }}</p>
                  </div>
                </div>

              </div>
            </div>

            <!-- Telemedicine: patient, referral source, outcome -->
            <div class="detail-card mb-4" v-if="isTele && tele">
              <div class="detail-card-header tele-header">
                <i class="fe fe-video mr-2"></i> Telemedicine
                <span class="badge badge-light ml-2" v-if="tele.billing_mode === 'claim'">Paid by claim</span>
                <span class="badge badge-light ml-2" v-else>Free consultation</span>
                <button v-if="['pending', 'accepted'].includes(appoint.data.status)" type="button"
                  class="btn btn-sm btn-light ml-auto" @click="rescheduleOpen = true">
                  <i class="fe fe-calendar mr-1"></i>Change doctor / date / time
                </button>
              </div>
              <div class="detail-card-body">
                <div class="info-row">
                  <div class="info-icon-wrap blue"><i class="fe fe-user"></i></div>
                  <div>
                    <p class="info-label">Patient</p>
                    <p class="info-value">{{ tele.data.patient_name }} <span class="text-muted small">({{ tele.data.patient_id_card }})</span></p>
                    <p class="small text-muted mb-0">
                      <span v-if="tele.data.patient_type === 'Dependent'">Dependant · </span>
                      <span v-if="tele.data.patient_gender">{{ tele.data.patient_gender }} · </span>
                      <span v-if="tele.data.patient_dob">DOB {{ tele.data.patient_dob }}</span>
                      <span v-if="tele.data.patient_phone"> · {{ tele.data.patient_phone }}</span>
                    </p>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-icon-wrap purple"><i class="fe fe-message-square"></i></div>
                  <div>
                    <p class="info-label">Reason</p>
                    <p class="info-value" style="white-space: pre-line;">{{ tele.data.reason }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-icon-wrap orange"><i class="fe fe-share-2"></i></div>
                  <div>
                    <p class="info-label">Sent by → Telemedicine provider</p>
                    <p class="info-value">
                      {{ tele.data.referring_provider ? tele.data.referring_provider.agency_name : 'Agency' }}
                      → {{ tele.data.provider ? tele.data.provider.agency_name : '—' }}
                    </p>
                  </div>
                </div>

                <!-- Encounter -->
                <div class="tele-section">
                  <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 8px;">
                    <div>
                      <p class="info-label mb-1">Encounter</p>
                      <p class="small mb-0" v-if="tele.data.service_summary_id">
                        Recorded<span v-if="tele.data.completed_at"> on {{ tele.data.completed_at }}</span>.
                      </p>
                      <p class="small text-muted mb-0" v-else>Not recorded yet.</p>
                    </div>
                    <div>
                      <router-link v-if="tele.data.service_summary_id" :to="`/encounter/${tele.data.service_summary_id}`" class="btn btn-sm btn-outline-primary">
                        <i class="fe fe-file-text mr-1"></i>View encounter
                      </router-link>
                      <router-link v-else-if="tele.is_consulting_doctor && appoint.data.status !== 'declined'"
                        :to="{ path: '/add-record', query: { appointment: appoint.data.id } }" class="btn btn-sm btn-create">
                        <i class="fe fe-file-plus mr-1"></i>Record encounter / refer
                      </router-link>
                    </div>
                  </div>
                </div>

                <!-- Referrals made in the encounter -->
                <div class="tele-section" v-if="tele.data.referrals && tele.data.referrals.length">
                  <p class="info-label mb-1">Referrals (authorization codes)</p>
                  <div class="small" v-for="r in tele.data.referrals" :key="r.id">
                    <i class="fe fe-corner-down-right mr-1"></i>{{ r.referred_to_facility || 'Facility' }}
                    · {{ r.services_count }} service(s)
                    <span v-if="r.code"> · code <strong>{{ r.code }}</strong></span>
                    <span class="text-muted"> · {{ r.status || 'pending' }}</span>
                  </div>
                </div>

                <!-- Claim (paid telemedicine) -->
                <div class="tele-section" v-if="tele.billing_mode === 'claim' && (tele.is_consulting_doctor || tele.data.claim)">
                  <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 8px;">
                    <div>
                      <p class="info-label mb-1">Claim</p>
                      <p class="small mb-0" v-if="tele.data.claim">
                        {{ tele.data.claim.claim_unique_id }} ·
                        <strong>{{ tele.data.claim.is_draft ? 'Draft (not yet submitted)' : (tele.data.claim.status || 'Submitted') }}</strong>
                      </p>
                      <p class="small text-muted mb-0" v-else-if="!tele.data.service_summary_id">Record the encounter first.</p>
                      <p class="small text-muted mb-0" v-else>No claim yet.</p>
                    </div>
                    <div class="d-flex" style="gap: 6px;" v-if="tele.is_consulting_doctor">
                      <button v-if="!tele.data.claim && tele.data.service_summary_id" type="button" class="btn btn-sm btn-create"
                        :disabled="isLoading" @click="createClaim">
                        <i class="fe fe-credit-card mr-1"></i>Create claim
                      </button>
                      <router-link v-if="tele.data.claim" :to="`/claim/${tele.data.claim.id}`" class="btn btn-sm btn-outline-primary">Review</router-link>
                      <button v-if="tele.data.claim && tele.data.claim.is_draft" type="button" class="btn btn-sm btn-create"
                        :disabled="isLoading" @click="submitClaim">
                        <i class="fe fe-send mr-1"></i>Submit for vetting
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Virtual Meeting Card -->
            <div class="virtual-card mb-4" v-if="appoint.data.type === 'virtual' && appoint.data.meeting_code">
              <div class="virtual-card-icon"><i class="fe fe-video"></i></div>
              <div class="flex-grow-1">
                <p class="virtual-label">Video Meeting Code</p>
                <p class="virtual-code">{{ appoint.data.meeting_code }}</p>
              </div>
              <div class="virtual-actions">
                <button class="btn btn-copy" @click="copyCode">
                  <i class="fe fe-copy mr-1"></i> Copy
                </button>
                <button
                  class="btn btn-join ml-2"
                  @click="joinMeeting"
                >
                  <i class="fe fe-video mr-1"></i> Join
                </button>
              </div>
            </div>

          </div>

          <!-- Right column: Consultation Notes -->
          <div class="col-lg-5 col-md-12">
            <div class="notes-panel">
              <div class="notes-panel-header">
                <div>
                  <h6 class="notes-title"><i class="mdi mdi-note-text mr-2"></i>Consultation Notes</h6>
                  <p class="notes-count">{{ (appoint.healthRecords || []).length }} note(s)</p>
                </div>
                <button
                  v-if="appoint.data.status !== 'completed' && appoint.data.status !== 'declined'"
                  class="btn btn-add-note"
                  data-toggle="modal"
                  data-target="#notesModal"
                >
                  <i class="fe fe-plus"></i> Add
                </button>
              </div>

              <div class="notes-empty" v-if="!appoint.healthRecords || !appoint.healthRecords.length">
                <i class="mdi mdi-note-outline" style="font-size:2rem;opacity:.3"></i>
                <p class="mt-2">No notes added yet</p>
              </div>

              <div
                class="note-item"
                v-for="(record, idx) in appoint.healthRecords"
                :key="record.id"
              >
                <div class="note-header">
                  <span class="note-number">Note #{{ idx + 1 }}</span>
                </div>
                <p class="note-section-label">Consultation</p>
                <p class="note-text">{{ record.notes }}</p>
                <div v-if="record.medications">
                  <p class="note-section-label mt-2">Medications</p>
                  <p class="note-text">{{ record.medications }}</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage" />
      </div>
    </main>

    <RescheduleConsultation v-if="tele" :open="rescheduleOpen" :consultation="appoint.data"
      @close="rescheduleOpen = false" @saved="fetchAppointment" />

    <!-- Notes Modal -->
    <div class="modal fade" id="notesModal" tabindex="-1" role="dialog" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div class="modal-content modal-styled">
          <div class="modal-header-styled">
            <div>
              <h5 class="modal-heading">Add Consultation Notes</h5>
              <p class="modal-sub">Document the consultation for this appointment</p>
            </div>
            <button type="button" class="modal-close-btn" data-dismiss="modal">
              <i class="fe fe-x"></i>
            </button>
          </div>
          <div class="modal-body px-4 pb-2">
            <div class="form-group">
              <label class="form-label">Consultation Notes <span class="text-danger">*</span></label>
              <textarea
                class="form-control styled-textarea"
                rows="5"
                v-model="notes"
                placeholder="Describe the consultation findings, observations, recommendations..."
              ></textarea>
            </div>
            <div class="form-group">
              <label class="form-label">Medications / Prescriptions</label>
              <textarea
                class="form-control styled-textarea"
                rows="3"
                v-model="medications"
                placeholder="List prescribed medications, dosages, instructions..."
              ></textarea>
            </div>
          </div>
          <div class="modal-footer-styled">
            <button type="button" class="btn btn-cancel" data-dismiss="modal">Cancel</button>
            <button class="btn btn-create" @click="createRecord" :disabled="isLoading">
              <span v-if="isLoading"><i class="mdi mdi-loading mdi-spin mr-1"></i>Saving...</span>
              <span v-else><i class="fe fe-save mr-1"></i>Save Notes</span>
            </button>
          </div>
        </div>
      </div>
    </div>

  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import RescheduleConsultation from "@/views/telemedicine/RescheduleConsultation.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

export default {
  components: { Navbar, Loading, RescheduleConsultation },
  data() {
    return {
      user: null,
      appoint: { data: null, healthRecords: [], professionalDetails: null },
      // Telemedicine detail (GET telemedicine/consultations/{id}) when the appointment is a consultation.
      tele: null,
      rescheduleOpen: false,
      notes: "",
      medications: "",
      isLoading: false,
      fullPage: true,
    };
  },
  computed: {
    isTele() {
      return !!(this.appoint.data && this.appoint.data.is_telemedicine);
    },
    hasActions() {
      const s = this.appoint.data ? this.appoint.data.status : null;
      return s === "pending" || s === "accepted";
    },
  },
  methods: {
    canTransition(targetStatus) {
      const current = this.appoint.data ? this.appoint.data.status : null;
      if (targetStatus === "accepted")  return current === "pending";
      if (targetStatus === "completed") return current === "accepted";
      if (targetStatus === "declined")  return current === "pending" || current === "accepted";
      return false;
    },
    fetchAppointment() {
      this.axios
        .get(`/api/v1/auth/getSpecificAppoint/${this.$route.params.id}`)
        .then((res) => {
          this.appoint = res.data;
          if (res.data.data && res.data.data.is_telemedicine) this.fetchTelemedicine();
        })
        .catch(console.error);
    },
    fetchTelemedicine() {
      this.axios
        .get(`/api/v1/auth/telemedicine/consultations/${this.$route.params.id}`)
        .then((res) => { this.tele = res.data; })
        .catch(console.error);
    },
    createClaim() {
      if (!confirm("Create a claim for this consultation? You can review it before submitting.")) return;
      this.isLoading = true;
      this.axios
        .post(`/api/v1/auth/telemedicine/consultations/${this.$route.params.id}/claim`)
        .then((res) => {
          this.$breadstick.notify(res.data.message || "Claim created", { position: "top-right" });
          this.fetchTelemedicine();
        })
        .catch((err) => {
          this.$breadstick.notify((err.response && err.response.data && err.response.data.message) || "Could not create the claim", { position: "top-right" });
        })
        .finally(() => { this.isLoading = false; });
    },
    submitClaim() {
      if (!confirm("Submit this claim to the agency for vetting?")) return;
      this.isLoading = true;
      this.axios
        .post(`/api/v1/auth/claims/${this.tele.data.claim.id}/submit`)
        .then(() => {
          this.$breadstick.notify("Claim submitted for vetting", { position: "top-right" });
          this.fetchTelemedicine();
        })
        .catch((err) => {
          this.$breadstick.notify((err.response && err.response.data && err.response.data.message) || "Could not submit the claim", { position: "top-right" });
        })
        .finally(() => { this.isLoading = false; });
    },
    updateStatus(newStatus) {
      if (!confirm(`Mark this appointment as "${newStatus}"?`)) return;
      this.isLoading = true;
      this.axios
        .put(`/api/v1/auth/appointments/${this.$route.params.id}`, { status: newStatus })
        .then(() => {
          this.$breadstick.notify(`Appointment marked as ${newStatus}`, { position: "top-right" });
          this.fetchAppointment();
        })
        .catch((err) => {
          console.error(err);
          this.$breadstick.notify("Failed to update status", { position: "top-right" });
        })
        .finally(() => { this.isLoading = false; });
    },
    createRecord() {
      if (!this.notes.trim()) {
        this.$breadstick.notify("Please enter consultation notes.", { position: "top-right" });
        return;
      }
      this.isLoading = true;
      this.axios
        .post("/api/v1/auth/addHealthRecord", {
          notes: this.notes,
          medications: this.medications,
          patient_id: this.appoint.data.patient_id,
          appointment_id: this.$route.params.id,
          professional_id: this.appoint.data.professional_id,
          provider_id: this.user.type === "provider" ? this.user.id : this.user.institutional_id,
          reasonVisit: "Consultation",
          testResult: "",
          drVisited: "",
          documents: "",
        })
        .then(() => {
          this.isLoading = false;
          this.notes = "";
          this.medications = "";
          this.fetchAppointment();
          this.$breadstick.notify("Notes saved successfully", { position: "top-right" });
          window.$('#notesModal').modal('hide');
        })
        .catch((err) => {
          console.error(err);
          this.isLoading = false;
          this.$breadstick.notify("Something went wrong", { position: "top-right" });
        });
    },
    joinMeeting() {
      const code = this.appoint.data.meeting_code;
      const id = this.$route.params.id;
      if (code) this.$router.push(`/video-call/${code}?apptId=${id}`);
    },
    copyCode() {
      navigator.clipboard.writeText(this.appoint.data.meeting_code).then(() => {
        this.$breadstick.notify("Meeting code copied!", { position: "top-right" });
      });
    },
  },
  created() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.fetchAppointment();
  },
};
</script>

<style scoped>
/* ── Hero ── */
.appt-hero {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  color: #fff;
}
.hero-title { font-size: 1.5rem; font-weight: 700; color: #fff; }
.back-link {
  display: inline-flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: 8px;
  background: rgba(255,255,255,.15); color: #fff; text-decoration: none;
  transition: background .2s;
}
.back-link:hover { background: rgba(255,255,255,.28); color: #fff; }

.hero-status-badge {
  display: inline-block; padding: 5px 14px; border-radius: 20px;
  font-size: .8rem; font-weight: 700; text-transform: capitalize;
}
.hero-status-badge.pending   { background: rgba(245,158,11,.25); color: #fde68a; }
.hero-status-badge.accepted  { background: rgba(59,130,246,.25); color: #bfdbfe; }
.hero-status-badge.completed { background: rgba(16,185,129,.25); color: #a7f3d0; }
.hero-status-badge.declined  { background: rgba(239,68,68,.25);  color: #fecaca; }

.tele-header { background: linear-gradient(135deg, #4c1d95, #7c3aed) !important; color: #fff !important; display: flex; align-items: center; flex-wrap: wrap; gap: 6px; }
.tele-section { border-top: 1px solid #f1f5f9; padding-top: 12px; margin-top: 12px; }
.hero-type {
  color: rgba(255,255,255,.7); font-size: .85rem;
  display: inline-flex; align-items: center; gap: 5px;
}

/* ── Buttons ── */
.btn-join {
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff; border: none; border-radius: 8px; padding: 9px 18px; font-weight: 600;
}
.btn-join:hover { opacity: .9; color: #fff; }
.btn-notes-header {
  background: rgba(255,255,255,.15); color: #fff; border: 1px solid rgba(255,255,255,.3);
  border-radius: 8px; padding: 9px 18px; font-weight: 600;
}
.btn-notes-header:hover { background: rgba(255,255,255,.25); color: #fff; }
.btn-copy {
  background: #f1f5f9; border: none; border-radius: 6px; padding: 6px 14px;
  font-size: .82rem; font-weight: 600; color: #475569;
}
.btn-copy:hover { background: #e2e8f0; }
.btn-add-note {
  background: #eff6ff; border: 1px solid #bfdbfe; color: #1d4ed8;
  border-radius: 7px; padding: 5px 12px; font-size: .82rem; font-weight: 600;
}
.btn-cancel { background: #f1f5f9; border: none; border-radius: 8px; padding: 10px 20px; color: #475569; font-weight: 600; }
.btn-create {
  background: linear-gradient(135deg, #2563eb, #7c3aed);
  color: #fff; border: none; border-radius: 8px; padding: 10px 20px; font-weight: 600;
}
.btn-create:hover { opacity:.9; color:#fff; }

/* ── Action Bar ── */
.action-bar {
  background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px;
  padding: 14px 20px; margin-bottom: 24px;
}
.action-bar-inner { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
.action-hint { margin: 0; font-size: .85rem; color: #92400e; font-weight: 500; }
.action-btns { display: flex; gap: 8px; flex-wrap: wrap; }
.action-btn {
  padding: 7px 18px; border: none; border-radius: 8px;
  font-size: .85rem; font-weight: 600; cursor: pointer; transition: all .2s;
  display: inline-flex; align-items: center;
}
.action-btn.accept   { background: #dbeafe; color: #1e40af; }
.action-btn.accept:hover   { background: #bfdbfe; }
.action-btn.complete { background: #d1fae5; color: #065f46; }
.action-btn.complete:hover { background: #a7f3d0; }
.action-btn.decline  { background: #fee2e2; color: #991b1b; }
.action-btn.decline:hover  { background: #fecaca; }
.action-btn:disabled { opacity: .5; cursor: not-allowed; }

/* ── Body ── */
.appt-body { padding-top: 28px; padding-bottom: 40px; }

/* ── Detail Card ── */
.detail-card {
  border-radius: 12px; border: none;
  box-shadow: 0 2px 12px rgba(0,0,0,.08); overflow: hidden;
}
.detail-card-header {
  background: #f8fafc; padding: 14px 20px;
  font-weight: 700; color: #1e3a5f; font-size: .95rem;
  border-bottom: 1px solid #e2e8f0;
}
.detail-card-body { padding: 8px 0; }

.info-row {
  display: flex; align-items: flex-start; gap: 14px;
  padding: 14px 20px; border-bottom: 1px solid #f1f5f9;
}
.info-row:last-child { border-bottom: none; }
.info-icon-wrap {
  width: 38px; height: 38px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1rem; flex-shrink: 0;
}
.info-icon-wrap.blue   { background: #dbeafe; color: #1d4ed8; }
.info-icon-wrap.purple { background: #ede9fe; color: #5b21b6; }
.info-icon-wrap.teal   { background: #ccfbf1; color: #0f766e; }
.info-icon-wrap.orange { background: #ffedd5; color: #c2410c; }
.info-label { font-size: .75rem; color: #94a3b8; text-transform: uppercase; letter-spacing: .05em; margin: 0 0 2px; font-weight: 600; }
.info-value { font-size: .95rem; color: #1e293b; font-weight: 500; margin: 0; }

/* ── Virtual Card ── */
.virtual-card {
  background: linear-gradient(135deg, #1e3a5f, #2563eb);
  border-radius: 12px; padding: 18px 20px;
  display: flex; align-items: center; gap: 16px; color: #fff;
  box-shadow: 0 4px 16px rgba(37,99,235,.3);
}
.virtual-card-icon {
  width: 44px; height: 44px; border-radius: 12px;
  background: rgba(255,255,255,.2);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.3rem; flex-shrink: 0;
}
.virtual-label { font-size: .75rem; opacity: .75; margin: 0 0 4px; text-transform: uppercase; letter-spacing: .06em; }
.virtual-code { font-family: monospace; font-size: 1rem; font-weight: 700; margin: 0; letter-spacing: .05em; }
.virtual-actions { display: flex; align-items: center; flex-shrink: 0; }

/* ── Notes Panel ── */
.notes-panel {
  border-radius: 12px; border: none;
  box-shadow: 0 2px 12px rgba(0,0,0,.08);
  background: #fff; overflow: hidden;
  margin-bottom: 30px;
}
.notes-panel-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 20px; border-bottom: 1px solid #f1f5f9; background: #f8fafc;
}
.notes-title { margin: 0; font-size: .9rem; font-weight: 700; color: #1e3a5f; }
.notes-count { font-size: .75rem; color: #94a3b8; margin: 2px 0 0; }
.notes-empty { padding: 40px 20px; text-align: center; color: #94a3b8; font-size: .85rem; }
.note-item {
  padding: 16px 20px; border-bottom: 1px solid #f1f5f9;
}
.note-item:last-child { border-bottom: none; }
.note-header { display: flex; justify-content: space-between; margin-bottom: 10px; }
.note-number { font-size: .75rem; font-weight: 700; color: #2563eb; background: #eff6ff; padding: 2px 8px; border-radius: 10px; }
.note-section-label { font-size: .73rem; color: #94a3b8; text-transform: uppercase; letter-spacing: .05em; font-weight: 600; margin: 0 0 4px; }
.note-text { font-size: .88rem; color: #374151; margin: 0; line-height: 1.6; }

/* ── Modal ── */
.modal-styled { border-radius: 16px; overflow: hidden; border: none; }
.modal-header-styled {
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 24px 28px 16px;
  background: linear-gradient(135deg, #1e3a5f, #2563eb);
  color: #fff;
}
.modal-heading { font-size: 1.1rem; font-weight: 700; margin: 0; color: #fff; }
.modal-sub { font-size: .82rem; opacity: .75; margin: 4px 0 0; }
.modal-close-btn {
  background: rgba(255,255,255,.15); border: none; border-radius: 8px;
  color: #fff; width: 32px; height: 32px; cursor: pointer;
}
.modal-close-btn:hover { background: rgba(255,255,255,.25); }
.modal-footer-styled {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 16px 24px; border-top: 1px solid #f1f5f9; background: #f8fafc;
}
.form-label { font-weight: 600; font-size: .85rem; color: #374151; margin-bottom: 6px; display: block; }
.styled-textarea {
  border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 10px 14px;
  font-size: .88rem; resize: vertical; transition: border-color .2s;
}
.styled-textarea:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); outline: none; }

/* ── Skeleton ── */
.skeleton-card {
  height: 300px; border-radius: 12px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
@keyframes shimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }
</style>
